/**
 * Outreach API Client
 *
 * This client replaces direct Mailgun API calls with secure backend API calls.
 * It maintains the same interface signatures as the original messaging service
 * to ensure seamless migration without breaking changes.
 */

import axios, { AxiosInstance, AxiosResponse } from "axios";
import { auth } from "../../../utils/firebase";
import { outreachApiConfig } from "../../../config/api.config";
import type {
  SendEmailOptions,
  CampaignStats,
  MessageTrackingData,
  EnhancedMessageTrackingData,
  IndividualEmailSendResponse,
  IndividualEmailRecipientResult,
  IndividualEmailBatchSummary,
  IndividualEmailStatus,
  EmailProvider,
  CampaignStatsProvider,
  ProviderCampaignStats,
  SesTrackingAggregateResponse,
  SesTrackingDailyAggregate,
} from "../types";

// API Configuration
const API_BASE_URL = outreachApiConfig.baseUrl;

const ensureIsoTimestamp = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
  }

  if (typeof value === "number") {
    const milliseconds = value > 1e12 ? value : value * 1000;
    const date = new Date(milliseconds);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  }

  if (typeof value === "string") {
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      return ensureIsoTimestamp(numeric);
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  }

  return undefined;
};

const mapSesEventTypeToTrackingStatus = (
  rawType?: string,
): EnhancedMessageTrackingData["status"] => {
  if (!rawType) {
    return "pending";
  }

  const normalized = rawType.toLowerCase();

  switch (normalized) {
    case "send":
    case "sent":
    case "sending":
    case "success":
    case "individual_send":
      return "sent";
    case "delivery":
    case "delivered":
      return "delivered";
    case "open":
    case "opened":
      return "opened";
    case "click":
    case "clicked":
      return "clicked";
    case "bounce":
    case "bounced":
    case "complaint":
    case "complained":
    case "failure":
    case "failed":
    case "reject":
    case "rejected":
      return "failed";
    default:
      return "pending";
  }
};

// Response interfaces matching backend API
interface OutreachApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

interface SendEmailResponse {
  messageId?: string;
  status?: "sent" | "queued";
  recipients?: string[];
  batchId?: string;
  results?: Array<{
    email: string;
    name?: string;
    status?: string;
    messageId?: string;
    error?: string;
    attemptCount?: number;
    updatedAt?: string;
    events?: Array<{
      type: string;
      timestamp: string;
      detail?: string;
    }>;
  }>;
  metrics?: {
    successCount?: number;
    failureCount?: number;
    totalCount?: number;
    startedAt?: string;
    completedAt?: string;
    averageSendDurationSeconds?: number;
  };
  rateLimit?: {
    perMinute?: number;
    burst?: number;
  };
  estimatedDurationSeconds?: number;
  errors?: string[];
}

interface TrackingDataResponse {
  campaignId: string;
  events: EnhancedMessageTrackingData[];
  totalCount: number;
  lastUpdated: string;
}

interface AggregatedTrackingResponse extends SesTrackingAggregateResponse {}

export interface CampaignStatsRequestOptions {
  provider?: CampaignStatsProvider;
  forceRefresh?: boolean;
}

/**
 * Bulk approval request payload
 */
export interface BulkApproveRequest {
  /** Array of channel IDs to approve (max 100) */
  channel_ids: string[];
  /** Target table enum (youtube_reaction or youtube_playlisting) */
  target_table: "youtube_reaction" | "youtube_playlisting";
  /** Optional notes to apply to all approved channels */
  notes?: string;
}

/**
 * Error details for individual channel validation or batch failures
 */
export interface BulkApproveChannelError {
  /** The channel ID that failed */
  channel_id: string;
  /** Error message describing why the channel failed */
  error: string;
}

/**
 * Bulk approval response data
 */
export interface BulkApproveResponseData {
  /** Number of channels successfully approved */
  approved: number;
  /** Number of channels that failed validation or batch write */
  failed: number;
  /** Array of error details for failed channels */
  errors: BulkApproveChannelError[];
}

/**
 * Outreach API Client Class
 * Handles all communication with the secure Outreach backend API
 */
export class OutreachApiClient {
  private axiosInstance: AxiosInstance;
  private authToken: string | null = null;
  private selectedProvider: EmailProvider | null = "ses";

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 300000, // 300 seconds (5 minutes) - matches Lambda timeout for bulk email operations
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add request interceptor to include auth token
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        const token = await this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        console.error("Request interceptor error:", error);
        return Promise.reject(error);
      },
    );

    // Add response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        // Enhanced error logging
        console.error("Outreach API Error:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          url: error.config?.url,
          timestamp: new Date().toISOString(),
        });

        // Handle specific error cases
        if (error.response?.status === 401) {
          console.warn("Authentication failed - token may be expired");
          this.authToken = null; // Clear cached token

          // Also clear localStorage tokens if they exist
          try {
            localStorage.removeItem("aws-cognito-tokens");
            console.log("Cleared expired tokens from localStorage");
          } catch (storageError) {
            console.warn(
              "Failed to clear tokens from localStorage:",
              storageError,
            );
          }

          // Dispatch custom event for authentication errors
          window.dispatchEvent(
            new CustomEvent("outreach-auth-error", {
              detail: { error, timestamp: new Date().toISOString() },
            }),
          );
        }

        // Handle rate limiting
        if (error.response?.status === 429) {
          console.warn("Rate limit exceeded - requests are being throttled");

          // Dispatch custom event for rate limiting
          window.dispatchEvent(
            new CustomEvent("outreach-rate-limit", {
              detail: {
                error,
                retryAfter: error.response?.headers["retry-after"],
                timestamp: new Date().toISOString(),
              },
            }),
          );
        }

        // Handle server errors
        if (error.response?.status >= 500) {
          console.error(
            "Server error detected - service may be temporarily unavailable",
          );

          // Dispatch custom event for server errors
          window.dispatchEvent(
            new CustomEvent("outreach-server-error", {
              detail: { error, timestamp: new Date().toISOString() },
            }),
          );
        }

        return Promise.reject(error);
      },
    );
  }

  private determineProviderFromRollout(): EmailProvider {
    return "ses";
  }

  private readProviderFromSession(): EmailProvider | null {
    return "ses";
  }

  private persistProvider(_provider: EmailProvider): void {
    // No-op: SES is enforced, provider selection is disabled
  }

  private getEmailProvider(): EmailProvider {
    this.selectedProvider = "ses";
    return "ses";
  }

  /**
   * Get authentication token from Firebase (required by backend)
   */
  private async getAuthToken(): Promise<string | null> {
    try {
      // Use cached token if available and not expired
      if (this.authToken) {
        return this.authToken;
      }

      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.warn("No authenticated user found");
        return null;
      }

      // The backend expects Firebase ID tokens, so we need to get a proper Firebase token
      // First, try the standard Firebase getIdToken method
      try {
        const idToken = await currentUser.getIdToken(true); // Force refresh
        this.authToken = idToken;
        console.log("Using Firebase ID token");
        console.log("Token preview:", idToken.substring(0, 50) + "...");
        console.log("User UID:", currentUser.uid);
        console.log("User email:", currentUser.email);
        return idToken;
      } catch (firebaseError) {
        console.warn("Firebase getIdToken failed:", firebaseError);

        // If the user is from the custom auth system, we need to sign them into Firebase
        // This is a temporary workaround until the auth systems are unified
        if ((currentUser as any).idToken && currentUser.email) {
          console.log(
            "Attempting to create Firebase custom token for Cognito user",
          );

          // For now, return null to indicate authentication failure
          // This will trigger the 401 error handling which clears tokens
          return null;
        }

        throw firebaseError;
      }
    } catch (error) {
      console.error("Failed to get auth token:", error);
      return null;
    }
  }

  /**
   * Send direct email (replaces sendEmails function)
   */
  async sendEmail(
    options: SendEmailOptions & { campaignId?: string },
  ): Promise<IndividualEmailSendResponse> {
    try {
      const {
        recipients,
        subject,
        body,
        testMode,
        testEmail,
        overrideToAddress,
        campaignId,
      } = options;

      // Validate inputs
      if (recipients.length === 0 && !testMode && !overrideToAddress) {
        return {
          success: false,
          message: "No recipients provided",
        };
      }

      // Prepare request payload to match Lambda function expectations
      const payload = {
        to: overrideToAddress
          ? overrideToAddress.split(',').map(email => email.trim())
          : recipients.map((r) => r.email),
        subject,
        html: body, // The Lambda function expects 'html' field for the email content
        text: body.replace(/<[^>]*>/g, ""), // Strip HTML for text version
        from: undefined, // Optional field
        tags: campaignId ? [campaignId] : undefined,
        campaignId: campaignId || "unknown",
      };

      const response: AxiosResponse<OutreachApiResponse<SendEmailResponse>> =
        await this.axiosInstance.post("/outreach/send-email", payload);

      return this.transformSendResponse(response.data, {
        recipientCount: recipients.length,
        overrideToAddress,
      });
    } catch (error) {
      console.error("Error sending email via API:", error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Unknown error sending email",
      };
    }
  }

  /**
   * Send template email (replaces sendMailgunEmail function)
   */
  async sendTemplateEmail(options: {
    recipients: Array<{ email: string; name?: string }>;
    subject: string;
    body: string;
    overrideToAddress?: string;
    campaignId?: string;
    tags?: string[];
  }): Promise<IndividualEmailSendResponse> {
    try {
      const { recipients, subject, body, overrideToAddress, campaignId, tags } =
        options;

      // Validate inputs
      if (recipients.length === 0 && !overrideToAddress) {
        return {
          success: false,
          message: "No recipients provided",
        };
      }

      // Prepare request payload to match Lambda function expectations
      const combinedTags = Array.from(
        new Set([
          ...(campaignId ? [campaignId] : []),
          ...(tags?.filter((tag) => tag && tag.trim()) ?? []),
        ]),
      );

      const payload = {
        to: overrideToAddress
          ? overrideToAddress.split(',').map(email => email.trim())
          : recipients.map((r) => r.email),
        template: body, // The Lambda function expects 'template' field for the email content
        templateVariables: {
          subject: subject,
          campaignId: campaignId || "unknown",
        },
        from: undefined, // Optional field
        tags: combinedTags.length > 0 ? combinedTags : undefined,
        campaignId: campaignId,
      };

      const response: AxiosResponse<OutreachApiResponse<SendEmailResponse>> =
        await this.axiosInstance.post(
          "/outreach/send-template-email-ses",
          payload,
        );

      return this.transformSendResponse(response.data, {
        recipientCount: recipients.length,
        overrideToAddress,
      });
    } catch (error) {
      console.error("Error sending template email via API:", error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Unknown error sending template email",
      };
    }
  }

  private mapStatus(status?: string): IndividualEmailStatus {
    switch ((status || "").toLowerCase()) {
      case "queued":
      case "queue":
        return "queued";
      case "sending":
        return "sending";
      case "delivered":
        return "delivered";
      case "opened":
        return "opened";
      case "clicked":
        return "clicked";
      case "failed":
      case "bounced":
      case "error":
        return "failed";
      case "sent":
      default:
        return "sent";
    }
  }

  private transformSendResponse(
    response: OutreachApiResponse<SendEmailResponse>,
    context: { recipientCount: number; overrideToAddress?: string },
  ): IndividualEmailSendResponse {
    const baseMessage =
      response.message ||
      (context.overrideToAddress && context.overrideToAddress.trim() !== ""
        ? `Email sent to override address: ${context.overrideToAddress}`
        : `Email sent to ${context.recipientCount} recipient(s)`);

    if (!response.success || !response.data) {
      return {
        success: false,
        message: response.message || response.error || "Failed to send email",
        errors: response.data?.errors,
      };
    }

    const data = response.data;
    const now = new Date().toISOString();

    type ApiEvent = NonNullable<
      IndividualEmailRecipientResult["events"]
    >[number];

    type ApiResult = NonNullable<SendEmailResponse["results"]>[number];

    const aggregatedResults: ApiResult[] = [];

    const appendResult = (item: unknown) => {
      if (!item || typeof item !== "object") {
        return;
      }

      const candidate = item as Record<string, unknown>;

      const rawEmail =
        typeof candidate.email === "string" && candidate.email.trim() !== ""
          ? candidate.email.trim()
          : typeof candidate.recipient === "string" &&
              candidate.recipient.trim() !== ""
            ? (candidate.recipient as string).trim()
            : undefined;

      if (!rawEmail) {
        return;
      }

      const rawStatus =
        typeof candidate.status === "string"
          ? candidate.status
          : typeof candidate.sendStatus === "string"
            ? (candidate.sendStatus as string)
            : undefined;

      const normalizedStatus = rawStatus ? rawStatus.toLowerCase() : undefined;

      const statusValue = normalizedStatus
        ? normalizedStatus === "success" || normalizedStatus === "succeeded"
          ? "sent"
          : normalizedStatus === "error"
            ? "failed"
            : normalizedStatus
        : candidate.error
          ? "failed"
          : "sent";

      const messageId =
        typeof candidate.messageId === "string" && candidate.messageId
          ? (candidate.messageId as string)
          : typeof candidate.sesMessageId === "string" && candidate.sesMessageId
            ? (candidate.sesMessageId as string)
            : data.messageId;

      const apiEvents: ApiEvent[] | undefined = Array.isArray(candidate.events)
        ? (candidate.events as unknown[])
            .map((rawEvent: unknown): ApiEvent | null => {
              if (!rawEvent || typeof rawEvent !== "object") {
                return null;
              }

              const eventData = rawEvent as Record<string, unknown>;
              const timestamp =
                typeof eventData.timestamp === "string"
                  ? eventData.timestamp
                  : undefined;

              if (!timestamp) {
                return null;
              }

              return {
                type: (eventData.type || "sent") as ApiEvent["type"],
                timestamp,
                detail: eventData.detail as ApiEvent["detail"],
              };
            })
            .filter((event): event is ApiEvent => Boolean(event))
        : undefined;

      aggregatedResults.push({
        email: rawEmail,
        name:
          typeof candidate.name === "string"
            ? (candidate.name as string)
            : undefined,
        status: statusValue,
        messageId,
        error:
          typeof candidate.error === "string"
            ? (candidate.error as string)
            : undefined,
        attemptCount:
          typeof candidate.attemptCount === "number"
            ? (candidate.attemptCount as number)
            : undefined,
        updatedAt:
          typeof candidate.updatedAt === "string"
            ? (candidate.updatedAt as string)
            : undefined,
        events: apiEvents,
      });
    };

    if (Array.isArray(data.results)) {
      data.results.forEach((result) => appendResult(result));
    } else if (data.results && typeof data.results === "object") {
      Object.values(data.results as Record<string, unknown>).forEach(
        (value: unknown) => {
          if (Array.isArray(value)) {
            value.forEach((item) => appendResult(item));
          } else if (value && typeof value === "object") {
            appendResult(value);
          }
        },
      );
    }

    const invalidRecipients = Array.isArray((data as any)?.invalidRecipients)
      ? (data as any).invalidRecipients
      : Array.isArray((data as any)?.results?.invalid)
        ? (data as any).results.invalid
        : [];

    invalidRecipients.forEach((email: unknown) => {
      if (typeof email === "string" && email.trim() !== "") {
        appendResult({
          recipient: email,
          status: "failed",
          error: "Invalid recipient",
        });
      }
    });

    if (aggregatedResults.length === 0 && data.results) {
      console.warn(
        "Unexpected results format from outreach API:",
        data.results,
      );
    }

    const normalizedResults =
      aggregatedResults.length > 0 ? aggregatedResults : undefined;

    const recipientResults: IndividualEmailRecipientResult[] | undefined =
      normalizedResults?.map((item) => ({
        email: item.email,
        name: item.name,
        status: this.mapStatus(item.status),
        messageId: item.messageId || data.messageId,
        error: item.error,
        attemptCount: item.attemptCount,
        lastUpdated: item.updatedAt || now,
        events: item.events?.map((event) => ({
          type: (event.type || "sent") as ApiEvent["type"],
          timestamp: event.timestamp,
          detail: event.detail,
        })),
      })) ??
      (Array.isArray(data.recipients) && data.recipients.length > 0
        ? data.recipients.map<IndividualEmailRecipientResult>((email) => ({
            email,
            status: this.mapStatus(data.status),
            messageId: data.messageId,
            lastUpdated: now,
          }))
        : context.overrideToAddress
          ? [
              {
                email: context.overrideToAddress,
                status: this.mapStatus(data.status),
                messageId: data.messageId,
                lastUpdated: now,
              },
            ]
          : undefined);

    const successCount =
      normalizedResults && normalizedResults.length > 0
        ? normalizedResults.filter(
            (result) => this.mapStatus(result.status) !== "failed",
          ).length
        : typeof (data as any)?.successful === "number"
          ? (data as any).successful
          : (data.metrics?.successCount ??
            (context.overrideToAddress ? 1 : context.recipientCount));

    const failureCount =
      normalizedResults && normalizedResults.length > 0
        ? normalizedResults.filter(
            (result) => this.mapStatus(result.status) === "failed",
          ).length
        : typeof (data as any)?.failed === "number"
          ? (data as any).failed
          : (data.metrics?.failureCount ?? 0);

    const total =
      normalizedResults && normalizedResults.length > 0
        ? normalizedResults.length
        : typeof (data as any)?.totalRecipients === "number"
          ? (data as any).totalRecipients
          : (data.metrics?.totalCount ??
            (context.overrideToAddress ? 1 : context.recipientCount));

    const batchSummary: IndividualEmailBatchSummary = {
      batchId: data.batchId || data.messageId || `batch_${Date.now()}`,
      startedAt: data.metrics?.startedAt || now,
      completedAt: data.metrics?.completedAt || now,
      total,
      successCount,
      failureCount,
      rateLimited: Boolean(data.rateLimit),
      estimatedDurationSeconds: data.estimatedDurationSeconds,
      averageSendDurationSeconds: data.metrics?.averageSendDurationSeconds,
    };

    return {
      success: true,
      message: baseMessage,
      batchSummary,
      recipientResults,
      errors: data.errors,
    };
  }

  private normalizeProviderStats(
    providerKey: CampaignStatsProvider,
    stats: ProviderCampaignStats | null | undefined,
    fallbackTimestamp: string,
  ): ProviderCampaignStats {
    const safeNumber = (value: unknown): number => {
      if (typeof value === "number" && Number.isFinite(value)) {
        return value;
      }
      if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
      }
      return 0;
    };

    const normalized: ProviderCampaignStats = {
      provider: stats?.provider ?? providerKey,
      totalSent: safeNumber(stats?.totalSent),
      delivered: safeNumber(stats?.delivered),
      opened: safeNumber(stats?.opened),
      clicked: safeNumber(stats?.clicked),
      bounced: safeNumber(stats?.bounced),
      complained: safeNumber(stats?.complained),
      unsubscribed: safeNumber(stats?.unsubscribed),
      deliveryRate: safeNumber(stats?.deliveryRate),
      openRate: safeNumber(stats?.openRate),
      clickRate: safeNumber(stats?.clickRate),
      bounceRate: safeNumber(stats?.bounceRate),
      complaintRate: safeNumber(stats?.complaintRate),
      engagementRate: stats?.engagementRate,
      clickToOpenRate: stats?.clickToOpenRate,
      lastUpdated: stats?.lastUpdated ?? fallbackTimestamp,
    };

    const delivered = normalized.delivered;
    const engagementRate =
      delivered > 0
        ? ((normalized.opened + normalized.clicked) / delivered) * 100
        : 0;
    const clickToOpenRate =
      normalized.opened > 0
        ? (normalized.clicked / normalized.opened) * 100
        : 0;

    return {
      ...normalized,
      engagementRate:
        normalized.engagementRate ?? Math.round(engagementRate * 100) / 100,
      clickToOpenRate:
        normalized.clickToOpenRate ?? Math.round(clickToOpenRate * 100) / 100,
    };
  }

  private normalizeCampaignStats(
    stats: CampaignStats | null | undefined,
    campaignId: string,
    provider?: CampaignStatsProvider,
  ): CampaignStats {
    const safeNumber = (value: unknown): number => {
      if (typeof value === "number" && Number.isFinite(value)) {
        return value;
      }
      if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
      }
      return 0;
    };

    const timestamp = stats?.lastUpdated ?? new Date().toISOString();

    const providers = stats?.providers
      ? (
          Object.entries(stats.providers) as Array<
            [CampaignStatsProvider, ProviderCampaignStats | null | undefined]
          >
        ).reduce<Partial<Record<CampaignStatsProvider, ProviderCampaignStats>>>(
          (acc, [key, value]) => {
            if (!value) {
              return acc;
            }

            // Ensure provider key is within expected range
            if (key !== "ses") {
              return acc;
            }

            return {
              ...acc,
              [key]: this.normalizeProviderStats(key, value, timestamp),
            };
          },
          {},
        )
      : undefined;

    const normalized: CampaignStats = {
      campaignId: stats?.campaignId ?? campaignId,
      totalSent: safeNumber(stats?.totalSent),
      delivered: safeNumber(stats?.delivered),
      opened: safeNumber(stats?.opened),
      clicked: safeNumber(stats?.clicked),
      bounced: safeNumber(stats?.bounced),
      complained: safeNumber(stats?.complained),
      unsubscribed: safeNumber(stats?.unsubscribed),
      deliveryRate: safeNumber(stats?.deliveryRate),
      openRate: safeNumber(stats?.openRate),
      clickRate: safeNumber(stats?.clickRate),
      bounceRate: safeNumber(stats?.bounceRate),
      complaintRate: safeNumber(stats?.complaintRate),
      provider: stats?.provider ?? provider ?? "ses",
      providers,
      engagementRate: stats?.engagementRate,
      clickToOpenRate: stats?.clickToOpenRate,
      lastUpdated: timestamp,
    };

    const delivered = normalized.delivered;
    const engagementRate =
      delivered > 0
        ? ((normalized.opened + normalized.clicked) / delivered) * 100
        : 0;
    const clickToOpenRate =
      normalized.opened > 0
        ? (normalized.clicked / normalized.opened) * 100
        : 0;

    return {
      ...normalized,
      engagementRate:
        normalized.engagementRate ?? Math.round(engagementRate * 100) / 100,
      clickToOpenRate:
        normalized.clickToOpenRate ?? Math.round(clickToOpenRate * 100) / 100,
    };
  }

  /**
   * Get campaign statistics (replaces getMailgunCampaignStats function)
   * Routes SES requests to client-side rate calculation using tracking aggregates
   */
  async getCampaignStats(
    campaignId: string,
    options: CampaignStatsRequestOptions = {},
  ): Promise<CampaignStats> {
    try {
      // Default provider to 'ses' if not specified
      const provider = options.provider || "ses";

      console.log(
        `Fetching campaign statistics for campaign ID: ${campaignId}`,
        `provider=${provider}`,
      );

      // Route SES requests through client-side calculation
      if (provider === "ses") {
        console.log(
          `Using client-side SES rate calculation for campaign ${campaignId}`,
        );
        const aggregates = await this.getSesTrackingAggregates(campaignId);
        return this.calculateRatesFromAggregates(aggregates, campaignId);
      }

      // Mailgun path: use existing API endpoint
      const params = new URLSearchParams();
      params.append("campaignId", campaignId);
      params.append("provider", provider);

      if (options.forceRefresh) {
        params.append("forceRefresh", "true");
      }

      const response: AxiosResponse<OutreachApiResponse<CampaignStats>> =
        await this.axiosInstance.get(
          `/outreach/campaign-stats?${params.toString()}`,
        );

      if (response.data.success && response.data.data) {
        const normalized = this.normalizeCampaignStats(
          response.data.data,
          campaignId,
          provider,
        );

        console.log(
          `Successfully retrieved campaign statistics for ${campaignId}:`,
          {
            totalSent: normalized.totalSent,
            deliveryRate: `${normalized.deliveryRate}%`,
            openRate: `${normalized.openRate}%`,
            clickRate: `${normalized.clickRate}%`,
            provider: normalized.provider,
          },
        );

        return normalized;
      } else {
        console.warn(`No statistics found for campaign ID: ${campaignId}`);

        return this.normalizeCampaignStats(null, campaignId, provider);
      }
    } catch (error) {
      console.error(
        `Error getting campaign statistics for ${campaignId}:`,
        error,
      );

      if (error instanceof Error) {
        throw new Error(`Failed to get campaign statistics: ${error.message}`);
      }
      throw new Error(
        "Unknown error occurred while fetching campaign statistics",
      );
    }
  }

  /**
   * Get SES tracking aggregates for a campaign (daily aggregation)
   * Returns an array of daily tracking data for rate calculations
   */
  async getSesTrackingAggregates(
    campaignId: string,
  ): Promise<SesTrackingDailyAggregate[]> {
    try {
      console.log(
        `Fetching SES tracking aggregates for campaign ID: ${campaignId}`,
      );

      const params = new URLSearchParams();
      params.append("aggregation", "daily");
      params.append("campaignId", campaignId);

      const response: AxiosResponse<
        OutreachApiResponse<AggregatedTrackingResponse>
      > = await this.axiosInstance.get(
        `/outreach/tracking-data-ses?${params.toString()}`,
      );

      if (response.data.success && response.data.data?.aggregates) {
        console.log(response);
        console.log(
          `Successfully retrieved ${response.data.data.aggregates.length} daily aggregates for ${campaignId}`,
        );
        return response.data.data.aggregates;
      } else {
        console.warn(
          `No tracking aggregates found for campaign ID: ${campaignId}`,
        );
        return [];
      }
    } catch (error) {
      console.error(
        `Error getting SES tracking aggregates for ${campaignId}:`,
        error,
      );

      if (error instanceof Error) {
        throw new Error(
          `Failed to get SES tracking aggregates: ${error.message}`,
        );
      }
      throw new Error(
        "Unknown error occurred while fetching SES tracking aggregates",
      );
    }
  }

  /**
   * Calculate campaign statistics from daily tracking aggregates
   * Uses industry-standard email rate formulas
   */
  private calculateRatesFromAggregates(
    aggregates: SesTrackingDailyAggregate[],
    campaignId: string,
  ): CampaignStats {
    // Sum up all metrics across daily aggregates
    const totals = aggregates.reduce(
      (acc, day) => ({
        sent: acc.sent + (day.sent || 0),
        delivered: acc.delivered + (day.delivered || 0),
        opened: acc.opened + (day.opened || 0),
        clicked: acc.clicked + (day.clicked || 0),
        bounced: acc.bounced + (day.bounced || 0),
        complained: acc.complained + (day.complained || 0),
      }),
      {
        sent: 0,
        delivered: 0,
        opened: 0,
        clicked: 0,
        bounced: 0,
        complained: 0,
      },
    );

    // Calculate rates using industry-standard formulas with zero-checks
    const deliveryRate =
      totals.sent > 0
        ? Math.round((totals.delivered / totals.sent) * 10000) / 100
        : 0;

    const openRate =
      totals.delivered > 0
        ? Math.round((totals.opened / totals.delivered) * 10000) / 100
        : 0;

    const clickRate =
      totals.delivered > 0
        ? Math.round((totals.clicked / totals.delivered) * 10000) / 100
        : 0;

    const bounceRate =
      totals.sent > 0
        ? Math.round((totals.bounced / totals.sent) * 10000) / 100
        : 0;

    const complaintRate =
      totals.delivered > 0
        ? Math.round((totals.complained / totals.delivered) * 10000) / 100
        : 0;

    const engagementRate =
      totals.delivered > 0
        ? Math.round(
            ((totals.opened + totals.clicked) / totals.delivered) * 10000,
          ) / 100
        : 0;

    const clickToOpenRate =
      totals.opened > 0
        ? Math.round((totals.clicked / totals.opened) * 10000) / 100
        : 0;

    return {
      campaignId,
      totalSent: totals.sent,
      delivered: totals.delivered,
      opened: totals.opened,
      clicked: totals.clicked,
      bounced: totals.bounced,
      complained: totals.complained,
      unsubscribed: 0, // SES tracking doesn't provide unsubscribe data
      deliveryRate,
      openRate,
      clickRate,
      bounceRate,
      complaintRate,
      provider: "ses",
      dailyAggregates: aggregates,
      engagementRate,
      clickToOpenRate,
      lastUpdated: new Date().toISOString(),
    };
  }

  /**
   * Transform raw Mailgun events into EnhancedMessageTrackingData format
   */
  private transformTrackingEvents(
    events: any[],
  ): EnhancedMessageTrackingData[] {
    const normalized: EnhancedMessageTrackingData[] = [];

    for (const event of events) {
      if (!event) {
        continue;
      }

      const sesRecord = this.transformSesTrackingRecord(event);
      if (sesRecord) {
        normalized.push(sesRecord);
      }
    }

    return normalized.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  }

  private transformSesTrackingRecord(
    event: any,
  ): EnhancedMessageTrackingData | null {
    const recipient =
      (typeof event.recipient === "string" && event.recipient) ||
      (typeof event.email === "string" && event.email) ||
      event?.sesEventDetail?.bounce?.bouncedRecipients?.[0]?.emailAddress ||
      "unknown";

    const baseTimestamp =
      ensureIsoTimestamp(event.sesEventTimestamp) ||
      ensureIsoTimestamp(event.timestamp) ||
      ensureIsoTimestamp(event.lastUpdated) ||
      new Date().toISOString();

    const messageId =
      (typeof event.sesMessageId === "string" && event.sesMessageId) ||
      (event.message?.headers?.["message-id"] as string | undefined) ||
      (typeof event.messageId === "string" && event.messageId) ||
      (typeof event.id === "string" && event.id) ||
      `${recipient}-${baseTimestamp}`;

    type TimelineEntry = NonNullable<
      EnhancedMessageTrackingData["events"]
    >[number];

    const timelineEntries: TimelineEntry[] = Array.isArray(event.events)
      ? (event.events as unknown[])
          .map((rawEntry: unknown): TimelineEntry | null => {
            if (!rawEntry || typeof rawEntry !== "object") {
              return null;
            }

            const entry = rawEntry as Record<string, unknown>;
            const entryTimestamp = ensureIsoTimestamp(entry.timestamp);
            if (!entryTimestamp) {
              return null;
            }

            const rawType =
              (typeof entry.type === "string" && entry.type) ||
              (typeof entry.eventType === "string" && entry.eventType) ||
              (typeof entry.event === "string" && entry.event) ||
              "event";

            return {
              signature:
                typeof entry.signature === "string"
                  ? entry.signature
                  : `${rawType}-${entryTimestamp}`,
              type: rawType.toLowerCase(),
              timestamp: entryTimestamp,
              detail:
                entry.detail && typeof entry.detail === "object"
                  ? (entry.detail as Record<string, unknown>)
                  : undefined,
              messageId:
                (typeof entry.messageId === "string" && entry.messageId) ||
                messageId,
              recipient:
                (typeof entry.recipient === "string" && entry.recipient) ||
                recipient,
              batchId:
                (typeof entry.batchId === "string" && entry.batchId) ||
                event.batchId,
            } satisfies TimelineEntry;
          })
          .filter((entry): entry is TimelineEntry => Boolean(entry))
          .sort(
            (a: TimelineEntry, b: TimelineEntry) =>
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
          )
      : [];

    const findTimelineTimestamp = (...types: string[]) =>
      timelineEntries.find((entry) => types.includes(entry.type))?.timestamp;

    const getString = (
      source: Record<string, unknown> | undefined,
      key: string,
    ): string | undefined =>
      source && typeof source[key] === "string"
        ? (source[key] as string)
        : undefined;

    const rawSendStatus =
      typeof event.sendStatus === "string"
        ? event.sendStatus.toLowerCase()
        : undefined;

    const statusSource =
      (typeof event.sesEventType === "string" && event.sesEventType) ||
      (timelineEntries.length > 0
        ? timelineEntries[timelineEntries.length - 1].type
        : undefined) ||
      (typeof event.event === "string" && event.event);

    let status = mapSesEventTypeToTrackingStatus(statusSource);

    if (rawSendStatus === "failed" || rawSendStatus === "error") {
      status = "failed";
    } else if (rawSendStatus === "success" || rawSendStatus === "succeeded") {
      status = "sent";
    }

    const bounceDetail = (event.sesEventDetail?.bounce ||
      timelineEntries.find((entry) => entry.type === "bounce")?.detail) as
      | Record<string, unknown>
      | undefined;
    const complaintDetail = (event.sesEventDetail?.complaint ||
      timelineEntries.find((entry) => entry.type === "complaint")?.detail) as
      | Record<string, unknown>
      | undefined;
    const deliveryDetail = (event.sesEventDetail?.delivery ||
      timelineEntries.find((entry) => entry.type === "delivery")?.detail) as
      | Record<string, unknown>
      | undefined;
    const openDetail = (event.sesEventDetail?.open ||
      timelineEntries.find((entry) => entry.type === "open")?.detail) as
      | Record<string, unknown>
      | undefined;
    const clickDetail = (event.sesEventDetail?.click ||
      timelineEntries.find((entry) => entry.type === "click")?.detail) as
      | Record<string, unknown>
      | undefined;

    const timelineSubjectEntry = timelineEntries.find((entry) => {
      const detail = entry.detail as Record<string, unknown> | undefined;
      const subjectValue = detail?.["subject"];
      return (
        typeof subjectValue === "string" &&
        (subjectValue as string).trim() !== ""
      );
    });

    const subjectFromTimeline = timelineSubjectEntry
      ? ((timelineSubjectEntry.detail as Record<string, unknown>)[
          "subject"
        ] as string)
      : undefined;

    const subject =
      typeof event.subject === "string" && event.subject.trim() !== ""
        ? event.subject
        : subjectFromTimeline ||
          (event.sesEventDetail?.mail?.commonHeaders?.subject as
            | string
            | undefined) ||
          `Campaign Email (${statusSource ?? "SES"})`;

    const enhanced: EnhancedMessageTrackingData = {
      id: event.id || messageId,
      recipient,
      subject,
      status,
      timestamp: baseTimestamp,
      mailgunMessageId: messageId,
      provider: "SES",
      sesEventType:
        typeof event.sesEventType === "string" ? event.sesEventType : undefined,
      sesEventTimestamp:
        ensureIsoTimestamp(event.sesEventTimestamp) || baseTimestamp,
      sesEventDetail: event.sesEventDetail,
      events: timelineEntries,
      lastUpdated: ensureIsoTimestamp(event.lastUpdated) || baseTimestamp,
    };

    const deliveredAt = findTimelineTimestamp("delivery", "delivered");
    if (deliveredAt) {
      enhanced.deliveredAt = deliveredAt;
    }

    const openedAt = findTimelineTimestamp("open", "opened");
    if (openedAt) {
      enhanced.openedAt = openedAt;
    }

    const clickedAt = findTimelineTimestamp("click", "clicked");
    if (clickedAt) {
      enhanced.clickedAt = clickedAt;
    }

    const bouncedAt = findTimelineTimestamp("bounce", "bounced");
    if (bouncedAt) {
      enhanced.bouncedAt = bouncedAt;
    }

    const complainedAt = findTimelineTimestamp("complaint", "complained");
    if (complainedAt) {
      enhanced.complainedAt = complainedAt;
    }

    const unsubscribedAt = findTimelineTimestamp("unsubscribe", "unsubscribed");
    if (unsubscribedAt) {
      enhanced.unsubscribedAt = unsubscribedAt;
    }

    const bounceDiagnostic = getString(bounceDetail, "diagnosticCode");
    const complaintFeedback = getString(
      complaintDetail,
      "complaintFeedbackType",
    );
    const smtpResponse = getString(deliveryDetail, "smtpResponse");

    if (bounceDiagnostic) {
      enhanced.error = bounceDiagnostic;
    } else if (complaintFeedback) {
      enhanced.error = `Complaint: ${complaintFeedback}`;
    } else if (smtpResponse) {
      enhanced.error = smtpResponse;
    }

    if (
      !enhanced.error &&
      typeof event.sendError === "string" &&
      event.sendError.trim() !== ""
    ) {
      enhanced.error = event.sendError;
    }

    const bounceReasonParts = [
      getString(bounceDetail, "bounceType"),
      getString(bounceDetail, "bounceSubType"),
    ].filter((value): value is string => Boolean(value));
    if (bounceReasonParts.length > 0) {
      enhanced.bounceReason = bounceReasonParts.join(" / ");
    }

    const userAgent =
      getString(clickDetail, "userAgent") ||
      getString(openDetail, "userAgent") ||
      (typeof event.userAgent === "string" && event.userAgent);
    if (userAgent) {
      enhanced.userAgent = userAgent;
    }

    if (!enhanced.subject) {
      enhanced.subject = `Campaign Email (${statusSource ?? "SES"})`;
    }

    return enhanced;
  }

  /**
   * Get tracking data for a campaign
   */
  async getTrackingData(
    campaignId: string,
    options: {
      limit?: number;
      offset?: number;
      status?: string;
    } = {},
  ): Promise<EnhancedMessageTrackingData[]> {
    try {
      console.log(`Fetching tracking data for campaign ID: ${campaignId}`);

      const params = new URLSearchParams();
      params.append("campaignId", campaignId);

      if (options.limit !== undefined) {
        params.append("limit", options.limit.toString());
      }
      if (options.offset !== undefined) {
        params.append("offset", options.offset.toString());
      }
      if (options.status) {
        params.append("status", options.status);
      }

      const response: AxiosResponse<OutreachApiResponse<TrackingDataResponse>> =
        await this.axiosInstance.get(
          `/outreach/tracking-data-ses?${params.toString()}`,
        );

      if (
        response.data.success &&
        response.data.data &&
        response.data.data.events
      ) {
        console.log(
          `Successfully retrieved ${response.data.data.events.length} tracking records for ${campaignId}`,
        );
        // Transform raw events into EnhancedMessageTrackingData format
        return this.transformTrackingEvents(response.data.data.events);
      } else {
        console.warn(
          `No tracking data found for campaign ID: ${campaignId}`,
          response.data,
        );
        return [];
      }
    } catch (error) {
      console.error(`Error getting tracking data for ${campaignId}:`, error);

      if (error instanceof Error) {
        throw new Error(`Failed to get tracking data: ${error.message}`);
      }
      throw new Error("Unknown error occurred while fetching tracking data");
    }
  }

  async getHistoricalTrackingAggregates(options: {
    campaignIds: string[];
    startDate?: string;
    endDate?: string;
    perCampaignLimit?: number;
  }): Promise<AggregatedTrackingResponse> {
    const { campaignIds, startDate, endDate, perCampaignLimit } = options;

    if (!campaignIds || campaignIds.length === 0) {
      throw new Error(
        "campaignIds are required to request aggregated SES tracking data",
      );
    }

    const params = new URLSearchParams();
    params.append("aggregation", "daily");
    params.append("campaignIds", campaignIds.join(","));

    if (startDate) {
      params.append("startDate", startDate);
    }

    if (endDate) {
      params.append("endDate", endDate);
    }

    if (perCampaignLimit) {
      params.append("perCampaignLimit", perCampaignLimit.toString());
    }

    try {
      const response: AxiosResponse<
        OutreachApiResponse<AggregatedTrackingResponse>
      > = await this.axiosInstance.get(
        `/outreach/tracking-data-ses?${params.toString()}`,
      );

      if (response.data.success && response.data.data?.aggregates) {
        return response.data.data;
      }

      throw new Error(
        response.data.error ||
          "Unable to retrieve SES tracking aggregates from outreach API",
      );
    } catch (error) {
      console.error("Error fetching SES tracking aggregates:", error);
      if (axios.isAxiosError(error)) {
        throw error;
      }
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(
        "Unknown error occurred while fetching SES tracking aggregates",
      );
    }
  }

  /**
   * Approve a pending channel and move it to a target table (youtube_reaction or youtube_playlisting)
   * @param channelId The document ID of the pending channel
   * @param targetTable The target table enum (youtube_reaction or youtube_playlisting)
   * @param notes Optional approval notes
   * @returns Object with success status, data (pending and approved channels), and message
   */
  async approvePendingChannel(
    channelId: string,
    targetTable: "youtube_reaction" | "youtube_playlisting",
    notes?: string,
  ): Promise<
    OutreachApiResponse<{
      pending_channel: any;
      approved_channel: any;
    }>
  > {
    try {
      // Validate inputs
      if (!channelId || channelId.trim() === "") {
        return {
          success: false,
          message: "Channel ID is required",
          error: "Invalid request: empty channel ID",
        };
      }

      if (!["youtube_reaction", "youtube_playlisting"].includes(targetTable)) {
        return {
          success: false,
          message:
            "Invalid target_table. Must be youtube_reaction or youtube_playlisting",
          error: "Invalid enum value",
        };
      }

      // Prepare request payload
      const payload = {
        target_table: targetTable,
        notes: notes || "",
      };

      // Make API request
      const response: AxiosResponse<OutreachApiResponse<any>> =
        await this.axiosInstance.post(
          `/outreach/pending-channels/${encodeURIComponent(channelId)}/approve`,
          payload,
        );

      // Handle successful response
      if (response.data.success) {
        return {
          success: true,
          message:
            response.data.message ||
            `Channel approved and added to ${targetTable}`,
          data: response.data.data,
        };
      }

      // Handle API error response
      return {
        success: false,
        message: response.data.message || "Failed to approve channel",
        error: response.data.error,
      };
    } catch (error) {
      // Handle axios errors
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const errorData = error.response?.data as any;

        // Handle specific error status codes
        if (status === 400) {
          return {
            success: false,
            message:
              errorData?.message ||
              "Invalid request: Missing email, invalid status, or invalid target_table",
            error: errorData?.error || "Validation error",
          };
        }

        if (status === 401) {
          return {
            success: false,
            message: "Authentication failed",
            error: "Unauthorized: Invalid or expired token",
          };
        }

        if (status === 404) {
          return {
            success: false,
            message: "Channel not found in pending_channels collection",
            error: "Not found",
          };
        }

        if (status === 409) {
          return {
            success: false,
            message: `Channel already exists in ${error.response?.data?.data?.target_table || "target table"}`,
            error: "Conflict: Duplicate channel",
          };
        }

        if (status === 500) {
          return {
            success: false,
            message: "Server error during approval process",
            error: errorData?.error || "Internal server error",
          };
        }

        // Generic axios error
        console.error("Error approving pending channel:", {
          status,
          statusText: error.response?.statusText,
          data: errorData,
          url: error.config?.url,
        });

        return {
          success: false,
          message: errorData?.message || `API error: ${error.message}`,
          error: error.message,
        };
      }

      // Handle non-axios errors
      console.error("Unexpected error approving pending channel:", error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Unknown error approving channel",
        error: "Unexpected error",
      };
    }
  }

  /**
   * Bulk approve multiple pending channels and move them to a target table
   *
   * Validation Strategy (all-or-nothing):
   * - Validates all channels exist, have email, are in correct status, and not duplicates
   * - If ANY validation fails, returns 400 with detailed error report (no channels approved)
   *
   * After validation passes:
   * - Uses Firestore batch writes for performance
   * - Tracks partial success if batch writes fail
   *
   * @param request The bulk approve request containing channel_ids, target_table, and optional notes
   * @returns Object with success status, approved/failed counts, and error details
   */
  async bulkApprovePendingChannels(
    request: BulkApproveRequest,
  ): Promise<OutreachApiResponse<BulkApproveResponseData>> {
    try {
      const { channel_ids, target_table, notes } = request;

      // Client-side validation: empty array
      if (
        !channel_ids ||
        !Array.isArray(channel_ids) ||
        channel_ids.length === 0
      ) {
        return {
          success: false,
          message: "channel_ids array is required and cannot be empty",
          error: "Validation error",
        };
      }

      // Client-side validation: max 100 channels
      if (channel_ids.length > 100) {
        return {
          success: false,
          message: `Too many channels: ${channel_ids.length}. Maximum allowed is 100 channels per request`,
          error: "Validation error",
        };
      }

      // Client-side validation: target_table enum
      if (!["youtube_reaction", "youtube_playlisting"].includes(target_table)) {
        return {
          success: false,
          message:
            "Invalid target_table. Must be youtube_reaction or youtube_playlisting",
          error: "Invalid enum value",
        };
      }

      // Client-side validation: check for empty strings in channel_ids
      const emptyIds = channel_ids.filter(
        (id) => !id || typeof id !== "string" || id.trim() === "",
      );
      if (emptyIds.length > 0) {
        return {
          success: false,
          message: `${emptyIds.length} channel ID(s) are empty or invalid`,
          error: "Validation error",
        };
      }

      // Prepare request payload
      const payload: BulkApproveRequest = {
        channel_ids,
        target_table,
        notes: notes || "",
      };

      // Make API request
      const response: AxiosResponse<
        OutreachApiResponse<BulkApproveResponseData>
      > = await this.axiosInstance.post(
        "/outreach/pending-channels/bulk-approve",
        payload,
      );

      // Handle successful response
      if (response.data.success) {
        const data = response.data.data;
        return {
          success: true,
          message:
            response.data.message ||
            `Approved ${data?.approved || 0} of ${channel_ids.length} channels`,
          data: data || { approved: 0, failed: 0, errors: [] },
        };
      }

      // Handle API error response (validation failed but returned 200)
      return {
        success: false,
        message: response.data.message || "Bulk approval failed",
        error: response.data.error,
        data: response.data.data,
      };
    } catch (error) {
      // Handle axios errors
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const errorData = error.response?.data as
          | OutreachApiResponse<BulkApproveResponseData>
          | undefined;

        // Handle 400 - Validation failures
        if (status === 400) {
          return {
            success: false,
            message:
              errorData?.message ||
              "Validation failed for one or more channels",
            error: errorData?.error || "Validation error",
            data: errorData?.data,
          };
        }

        // Handle 401 - Authentication failed
        if (status === 401) {
          return {
            success: false,
            message: "Authentication failed",
            error: "Unauthorized: Invalid or expired token",
          };
        }

        // Handle 409 - All channels are duplicates
        if (status === 409) {
          return {
            success: false,
            message:
              errorData?.message ||
              "All channels already exist in target table",
            error: "Conflict: Duplicate channels",
            data: errorData?.data,
          };
        }

        // Handle 500 - Server error
        if (status === 500) {
          return {
            success: false,
            message: "Server error during bulk approval process",
            error: errorData?.error || "Internal server error",
            data: errorData?.data,
          };
        }

        // Generic axios error
        console.error("Error bulk approving pending channels:", {
          status,
          statusText: error.response?.statusText,
          data: errorData,
          url: error.config?.url,
        });

        return {
          success: false,
          message: errorData?.message || `API error: ${error.message}`,
          error: error.message,
          data: errorData?.data,
        };
      }

      // Handle non-axios errors
      console.error("Unexpected error bulk approving pending channels:", error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Unknown error during bulk approval",
        error: "Unexpected error",
      };
    }
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.axiosInstance.get("/health");
      return response.status === 200;
    } catch (error) {
      console.error("Health check failed:", error);
      return false;
    }
  }

  /**
   * Reject a pending channel with reason and optional notes
   * @param channelId The document ID of the pending channel
   * @param reason The rejection reason enum (not_relevant, duplicate, invalid_channel, low_quality, other)
   * @param notes Optional rejection notes (required if reason is 'other')
   * @returns Object with success status, data (updated channel), and message
   */
  async rejectPendingChannel(
    channelId: string,
    reason:
      | "not_relevant"
      | "duplicate"
      | "invalid_channel"
      | "low_quality"
      | "other",
    notes?: string,
  ): Promise<OutreachApiResponse<any>> {
    try {
      // Validate inputs
      if (!channelId || channelId.trim() === "") {
        return {
          success: false,
          message: "Channel ID is required",
          error: "Invalid request: empty channel ID",
        };
      }

      const validReasons = [
        "not_relevant",
        "duplicate",
        "invalid_channel",
        "low_quality",
        "other",
      ];
      if (!validReasons.includes(reason)) {
        return {
          success: false,
          message: `Invalid reason. Must be one of: ${validReasons.join(", ")}`,
          error: "Invalid enum value",
        };
      }

      // If reason is 'other', notes must be provided
      if (reason === "other" && (!notes || notes.trim() === "")) {
        return {
          success: false,
          message: 'Notes are required when rejection reason is "other"',
          error: "Validation error",
        };
      }

      // Prepare request payload
      const payload = {
        reason,
        notes: notes || "",
      };

      // Make API request
      const response: AxiosResponse<OutreachApiResponse<any>> =
        await this.axiosInstance.post(
          `/outreach/pending-channels/${encodeURIComponent(channelId)}/reject`,
          payload,
        );

      // Handle successful response
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || "Channel rejected",
          data: response.data.data,
        };
      }

      // Handle API error response
      return {
        success: false,
        message: response.data.message || "Failed to reject channel",
        error: response.data.error,
      };
    } catch (error) {
      // Handle axios errors
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const errorData = error.response?.data as any;

        // Handle specific error status codes
        if (status === 400) {
          return {
            success: false,
            message:
              errorData?.message ||
              'Invalid request: Invalid reason or missing notes for "other" reason',
            error: errorData?.error || "Validation error",
          };
        }

        if (status === 401) {
          return {
            success: false,
            message: "Authentication failed",
            error: "Unauthorized: Invalid or expired token",
          };
        }

        if (status === 404) {
          return {
            success: false,
            message: "Channel not found in pending_channels collection",
            error: "Not found",
          };
        }

        if (status === 500) {
          return {
            success: false,
            message: "Server error during rejection process",
            error: errorData?.error || "Internal server error",
          };
        }

        // Generic axios error
        console.error("Error rejecting pending channel:", {
          status,
          statusText: error.response?.statusText,
          data: errorData,
          url: error.config?.url,
        });

        return {
          success: false,
          message: errorData?.message || `API error: ${error.message}`,
          error: error.message,
        };
      }

      // Handle non-axios errors
      console.error("Unexpected error rejecting pending channel:", error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Unknown error rejecting channel",
        error: "Unexpected error",
      };
    }
  }

  /**
   * Clear cached authentication token (useful for forcing token refresh)
   */
  clearAuthToken(): void {
    this.authToken = null;
    console.log("Cleared cached authentication token");
  }

  /**
   * Force refresh of authentication token
   */
  async refreshAuthToken(): Promise<string | null> {
    this.authToken = null; // Clear cache
    return await this.getAuthToken();
  }

  /**
   * Test authentication without making a full API call
   */
  async testAuthentication(): Promise<{
    isValid: boolean;
    error?: string;
    tokenInfo?: any;
  }> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        return { isValid: false, error: "No authentication token available" };
      }

      // Decode the JWT token to see its contents (for debugging)
      let tokenInfo = {};
      try {
        const tokenParts = token.split(".");
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          tokenInfo = {
            iss: payload.iss, // issuer
            aud: payload.aud, // audience
            exp: payload.exp, // expiration
            iat: payload.iat, // issued at
            sub: payload.sub, // subject (user ID)
            email: payload.email,
            email_verified: payload.email_verified,
          };
          console.log("Token payload:", tokenInfo);
        }
      } catch (decodeError) {
        console.warn("Could not decode token:", decodeError);
      }

      // Make a lightweight request to test auth
      const response = await this.axiosInstance.get("/health");
      return {
        isValid: response.status === 200,
        tokenInfo,
      };
    } catch (error: any) {
      return {
        isValid: false,
        error:
          error.response?.status === 401
            ? "Authentication failed"
            : "Network error",
        tokenInfo: {},
      };
    }
  }

  /**
   * Get current API status and configuration
   */
  getApiStatus(): {
    baseUrl: string;
    hasAuthToken: boolean;
    lastError?: string;
  } {
    return {
      baseUrl: API_BASE_URL,
      hasAuthToken: !!this.authToken,
    };
  }
}

// Create and export a singleton instance
export const outreachApiClient = new OutreachApiClient();

// Export individual functions that match the original messaging.ts interface
export const sendEmails = (
  options: SendEmailOptions & { campaignId?: string },
) => outreachApiClient.sendEmail(options);

export const sendMailgunEmail = (options: {
  recipients: Array<{ email: string; name?: string }>;
  subject: string;
  body: string;
  overrideToAddress?: string;
  campaignId?: string;
  tags?: string[];
}) => outreachApiClient.sendTemplateEmail(options);

export const getMailgunCampaignStats = (
  campaignId: string,
  options?: CampaignStatsRequestOptions,
) => outreachApiClient.getCampaignStats(campaignId, options);

export const getEnhancedMessageTrackingData = (
  campaignId: string,
  options: {
    forceRefresh?: boolean;
    enableValidation?: boolean;
    maxCacheAge?: number;
  } = {},
) => {
  // Transform the options to match the API client interface
  return outreachApiClient
    .getTrackingData(campaignId, {
      // Map the options to API parameters as needed
      limit: 300, // Maximum allowed limit
    })
    .then((messages) => ({
      data: messages,
      metadata: {
        campaignId,
        totalCount: messages.length,
        lastUpdated: new Date().toISOString(),
        forceRefresh: options.forceRefresh,
        enableValidation: options.enableValidation,
      },
    }));
};

export const getSesTrackingAggregates = (
  campaignIds: string[],
  options?: {
    startDate?: string;
    endDate?: string;
    perCampaignLimit?: number;
  },
) =>
  outreachApiClient.getHistoricalTrackingAggregates({
    campaignIds,
    startDate: options?.startDate,
    endDate: options?.endDate,
    perCampaignLimit: options?.perCampaignLimit,
  });

export const approvePendingChannel = (
  channelId: string,
  targetTable: "youtube_reaction" | "youtube_playlisting",
  notes?: string,
) => outreachApiClient.approvePendingChannel(channelId, targetTable, notes);

export default outreachApiClient;
